import { trackChanges } from './scheduleNotices';
import { syncTracking } from './presence';
import { servedByRole } from './roleRules';
import type { EventDraft } from './transitions';
import type { Ministry, ScheduleEvent } from './types';

/**
 * Edição de evento sem apagar a história. Cada data de uma série cai num grupo:
 *
 * - `futureEmpty` — ainda não aconteceu e não tem ninguém escalado: sempre recebe a edição;
 * - `futureScheduled` — ainda não aconteceu e já tem escala: recebe se a pessoa escolher;
 * - `pastEmpty` — já aconteceu sem ninguém marcado (como as ocorrências passadas recém
 *   criadas): recebe se a pessoa escolher;
 * - `pastServed` — já aconteceu e tem quem serviu marcado: **a composição nunca muda** —
 *   na série recebe só o título; editada sozinha, aceita título, ministério ou função a
 *   mais e quantidade maior, mas nada que tire quem serviu (nem o horário, que já passou).
 */

export type EditGroup = 'futureEmpty' | 'futureScheduled' | 'pastEmpty' | 'pastServed';

export interface EditOptions {
  today: string;
  /** Aplica também às datas futuras que já têm escala. */
  includeScheduled: boolean;
  /** Aplica também às datas passadas ainda sem registro. */
  includePastEmpty: boolean;
}

const hasPeople = (slots: Record<string, string[]>) => Object.values(slots).some((ids) => ids.length > 0);

export function editGroupOf(event: ScheduleEvent, today: string): EditGroup {
  if (event.date < today) return hasPeople(servedByRole(event)) ? 'pastServed' : 'pastEmpty';

  return hasPeople(event.slots) ? 'futureScheduled' : 'futureEmpty';
}

type Mode = 'full' | 'title' | 'none';

/**
 * As datas que a edição alcança: só a editada (`one`), ela e as seguintes da série
 * (`following`, pela data e hora da editada) ou a série toda (`series`).
 */
function inScope(event: ScheduleEvent, draft: EventDraft, anchor: ScheduleEvent | undefined): boolean {
  if (draft.scope === 'one' || !draft.isRecurring) return event.id === draft.eventId;
  if (event.seriesId !== draft.seriesId) return false;

  return draft.scope === 'series' || !anchor || event.date + event.time >= anchor.date + anchor.time;
}

const anchorOf = (events: readonly ScheduleEvent[], draft: EventDraft) =>
  events.find((e) => e.id === draft.eventId);

/** O que a edição faz com cada data: tudo, só o título ou nada. */
function modeOf(
  event: ScheduleEvent,
  draft: EventDraft,
  options: EditOptions,
  anchor: ScheduleEvent | undefined
): Mode {
  const one = draft.scope === 'one' || !draft.isRecurring;

  if (!inScope(event, draft, anchor)) return 'none';
  if (one) return 'full';

  const group = editGroupOf(event, options.today);

  if (group === 'pastServed') return 'title';
  if (group === 'futureScheduled' && !options.includeScheduled) return 'none';
  if (group === 'pastEmpty' && !options.includePastEmpty) return 'none';

  return 'full';
}

/** As datas da edição, por grupo (ids), na ordem das datas. */
export function editGroups(
  events: readonly ScheduleEvent[],
  draft: EventDraft,
  today: string
): Record<EditGroup, string[]> {
  const groups: Record<EditGroup, string[]> = {
    futureEmpty: [],
    futureScheduled: [],
    pastEmpty: [],
    pastServed: [],
  };
  const anchor = anchorOf(events, draft);

  for (const e of events) {
    if (inScope(e, draft, anchor)) groups[editGroupOf(e, today)].push(e.id);
  }

  return groups;
}

/**
 * Aplica o rascunho a uma data. Na data com quem serviu (`protect`), nada que tire alguém:
 * o ministério e a função em que alguém serviu ficam, e o horário não muda. Se a edição
 * tirar alguém da escala, a escala volta a rascunho.
 */
function applyDraft(
  event: ScheduleEvent,
  draft: EventDraft,
  ministries: readonly Ministry[],
  protect: boolean
): ScheduleEvent {
  const roleIdsOf = (ministryId: string) =>
    ministries.find((m) => m.id === ministryId)?.roles.map((r) => r.id) ?? [];
  const served = protect ? servedByRole(event) : {};
  const keep = (roleId: string) => (served[roleId] ?? []).length > 0;
  const kept = event.ministryIds.filter((id) => !draft.ministryIds.includes(id) && roleIdsOf(id).some(keep));
  const ministryIds = [...draft.ministryIds, ...kept];
  const need: Record<string, number> = { ...draft.need };

  for (const id of kept) {
    for (const roleId of roleIdsOf(id)) {
      if (event.need[roleId] !== undefined) need[roleId] = event.need[roleId];
    }
  }

  for (const [roleId, qty] of Object.entries(need)) {
    if (qty !== 0 || !keep(roleId)) continue;
    if (event.need[roleId]) need[roleId] = event.need[roleId];
    else delete need[roleId];
  }

  const slots = { ...event.slots };

  event.ministryIds
    .filter((id) => !ministryIds.includes(id))
    .forEach((id) => roleIdsOf(id).forEach((r) => delete slots[r]));
  Object.entries(need).forEach(([roleId, qty]) => {
    if (qty === 0) delete slots[roleId];
  });

  const removed = Object.values(event.slots).flat().length > Object.values(slots).flat().length;

  // Tirar alguém volta a rascunho; quem saiu fica anotado e é avisado ao publicar de novo.
  return trackChanges(
    event,
    syncTracking({
      ...event,
      title: draft.title.trim(),
      time: protect ? event.time : draft.time,
      ministryIds,
      need,
      slots,
      published: event.published && !removed,
    })
  );
}

/** Aplica a edição de evento (uma data ou a série) conforme os grupos e as escolhas. */
export function applyEventEdit(
  events: ScheduleEvent[],
  draft: EventDraft,
  ministries: readonly Ministry[],
  options: EditOptions
): ScheduleEvent[] {
  const anchor = anchorOf(events, draft);

  return events.map((original) => {
    // A cor não mexe na escala: vale em toda data do alcance, mesmo nas que ficam como
    // estão ou recebem só o título, para a série não ficar com cores misturadas.
    const e =
      draft.color !== undefined && inScope(original, draft, anchor)
        ? { ...original, color: draft.color }
        : original;
    const mode = modeOf(e, draft, options, anchor);

    if (mode === 'none') return e;
    if (mode === 'title') return { ...e, title: draft.title.trim() };

    const protect = editGroupOf(e, options.today) === 'pastServed';
    const next = applyDraft(e, draft, ministries, protect);
    const one = draft.scope === 'one' || !draft.isRecurring;

    if (!one) return next;

    return { ...next, date: protect ? e.date : draft.date || e.date, edited: e.recurrence ? true : e.edited };
  });
}

export interface RoleEditImpact {
  roleId: string;
  ministryId: string;
  /** Quantidade nova (a padrão da função quando não ajustada). */
  qty: number;
  /** Quantidade de antes (a da última data afetada). */
  from: number;
  /** Datas com escala em que a quantidade aumenta (abre vagas). */
  increased: number;
  /** Datas e pessoas que passam a ficar como extras (mais gente que a quantidade). */
  extraDates: number;
  extras: number;
  /** Datas e pessoas tiradas da escala (função zerada). */
  removedDates: number;
  removed: number;
}

export interface MinistryEditImpact {
  ministryId: string;
  removedDates: number;
  removed: number;
}

export interface EditImpact {
  groups: Record<EditGroup, string[]>;
  /** Datas que recebem a edição inteira. */
  applied: string[];
  /** Datas que recebem só o título (passadas com quem serviu). */
  titleOnly: string[];
  roles: RoleEditImpact[];
  /** Ministérios desmarcados que tiram gente da escala. */
  ministries: MinistryEditImpact[];
  removed: number;
  /** Escalas publicadas que voltam a rascunho (alguém sai). */
  unpublished: number;
}

/** O que a edição muda de fato, para o diálogo e a confirmação mostrarem antes de salvar. */
export function editImpact(
  events: readonly ScheduleEvent[],
  draft: EventDraft,
  ministries: readonly Ministry[],
  options: EditOptions
): EditImpact {
  const anchor = anchorOf(events, draft);
  const targets = events.filter((e) => modeOf(e, draft, options, anchor) !== 'none');
  const applied = targets.filter((e) => modeOf(e, draft, options, anchor) === 'full');
  const after = new Map(applyEventEdit([...applied], draft, ministries, options).map((e) => [e.id, e]));
  const roles = new Map<string, RoleEditImpact>();
  const removedByMinistry = new Map<string, MinistryEditImpact>();
  let removed = 0;
  let unpublished = 0;

  for (const before of applied) {
    const next = after.get(before.id)!;
    let lost = 0;

    for (const ministryId of before.ministryIds) {
      const ministry = ministries.find((m) => m.id === ministryId);

      for (const role of ministry?.roles ?? []) {
        const had = (before.slots[role.id] ?? []).length;
        const has = (next.slots[role.id] ?? []).length;
        const qty = next.need[role.id] ?? role.qty;
        const oldQty = before.need[role.id] ?? role.qty;
        const gone = had - has;

        lost += gone;

        if (!next.ministryIds.includes(ministryId)) {
          if (gone > 0) {
            const m = removedByMinistry.get(ministryId) ?? { ministryId, removedDates: 0, removed: 0 };

            removedByMinistry.set(ministryId, { ...m, removed: m.removed + gone });
          }
          continue;
        }

        const r = roles.get(role.id) ?? {
          roleId: role.id,
          ministryId,
          qty,
          from: oldQty,
          increased: 0,
          extraDates: 0,
          extras: 0,
          removedDates: 0,
          removed: 0,
        };

        if (gone > 0) {
          r.removedDates++;
          r.removed += gone;
        } else if (has > qty && qty > 0) {
          r.extraDates++;
          r.extras += has - qty;
        } else if (had > 0 && qty > oldQty) {
          r.increased++;
          r.from = oldQty;
        }

        roles.set(role.id, r);
      }
    }

    for (const ministryId of before.ministryIds) {
      const m = removedByMinistry.get(ministryId);
      const roleIds = ministries.find((x) => x.id === ministryId)?.roles.map((r) => r.id) ?? [];

      if (m && roleIds.some((id) => (before.slots[id] ?? []).length > (next.slots[id] ?? []).length)) {
        m.removedDates++;
      }
    }

    removed += lost;
    if (before.published && lost > 0) unpublished++;
  }

  return {
    groups: editGroups(events, draft, options.today),
    applied: applied.map((e) => e.id),
    titleOnly: targets.filter((e) => modeOf(e, draft, options, anchor) === 'title').map((e) => e.id),
    roles: [...roles.values()].filter((r) => r.removed || r.extras || r.increased),
    ministries: [...removedByMinistry.values()],
    removed,
    unpublished,
  };
}

/**
 * A edição tira alguém que já serviu numa data passada? A interface já impede; a API recusa
 * (409) quem tentar por fora. Vale para a data editada sozinha (na série, essas datas só
 * recebem o título).
 */
export function removesHistory(
  event: ScheduleEvent,
  draft: EventDraft,
  ministries: readonly Ministry[],
  today: string
): boolean {
  if (editGroupOf(event, today) !== 'pastServed') return false;

  const served = servedByRole(event);
  const roleIdsOf = (ministryId: string) =>
    ministries.find((m) => m.id === ministryId)?.roles.map((r) => r.id) ?? [];

  return Object.entries(served).some(([roleId, ids]) => {
    if (!ids.length) return false;

    const ministryId = event.ministryIds.find((id) => roleIdsOf(id).includes(roleId));

    return !ministryId || !draft.ministryIds.includes(ministryId) || draft.need[roleId] === 0;
  });
}
